import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../Venues/VenueShow.css";
import { Grid } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "../../contexts/AuthContext";
import {
  addFavorite,
  getMyFavorites,
  removeFavorite,
} from "../../utils/favoritesApi";
import { AnimatePresence, motion } from "framer-motion";
import { applyDynamicSeo } from "../../utils/dynamicSeo";
import {
  getProductDetail,
  submitLead,
  trackContactClick,
} from "../../utils/clientAccessApi";

const formatPrice = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed).toLocaleString("mk-MK");
};

const defaultInquiryForm = {
  name: "",
  email: "",
  phone: "",
  wedding_date: "",
  budget: "",
  notes: "",
};

const motionItem = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const motionSection = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

function ProductShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authPayload } = useAuth();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteError, setFavoriteError] = useState("");
  const [favoriteNotice, setFavoriteNotice] = useState("");
  const [inquiryForm, setInquiryForm] = useState(defaultInquiryForm);
  const [inquiryStatus, setInquiryStatus] = useState({
    busy: false,
    error: "",
    success: "",
  });

  const productPrice = product ? formatPrice(product.price) : null;
  const productVenueId = product?.venue_record_id || product?.venue_id;
  const isAvailable =
    product?.available === true ||
    product?.available === 1 ||
    product?.available === "true";
  const productImages =
    product?.images?.length > 0
      ? product.images
      : ["https://placehold.co/600x400/weddings/white"];
  const packages = Array.isArray(product?.packages) ? product.packages : [];
  const addOns = Array.isArray(product?.add_ons) ? product.add_ons : [];
  const contactCtas = Array.isArray(product?.contact_ctas) ? product.contact_ctas : [];

  useEffect(() => {
    let isMounted = true;

    const loadProductData = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getProductDetail(id);

        if (!isMounted) {
          return;
        }

        setProduct(data?.product || null);
        setProducts(Array.isArray(data?.similar_products) ? data.similar_products : []);
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        console.error("Error fetching product details:", fetchError);
        setError(fetchError.message || "Не успеавме да ги вчитаме деталите за продуктот.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProductData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id]);

  useEffect(() => {
    if (!product?.name) {
      return;
    }

    applyDynamicSeo({
      title: `${product.name} | Kazhavda`,
      description:
        product.description ||
        "Погледнете детали за свадбен продукт, пакети и дополнителни опции.",
      imageUrl: product.images?.[0],
      type: "product",
      keywords: `свадбен продукт, ${product.name}, wedding product, kazhavda`,
    });
  }, [product]);

  useEffect(() => {
    if (!favoriteNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFavoriteNotice("");
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [favoriteNotice]);

  useEffect(() => {
    if (!authPayload?.id_token || !product?.id) {
      setIsFavorite(false);
      return undefined;
    }

    let isMounted = true;

    const loadFavoriteState = async () => {
      try {
        setFavoriteError("");
        const payload = await getMyFavorites();
        const productIdAsString = String(product.id);
        const productIds = Array.isArray(payload?.product_ids)
          ? payload.product_ids.map((value) => String(value))
          : [];
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const fallbackFavorites = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.favorites)
            ? payload.favorites
            : [];

        const favorited =
          productIds.includes(productIdAsString) ||
          items.some(
            (item) =>
              item?.favorite_type === "product" &&
              String(item?.item_id) === productIdAsString
          ) ||
          fallbackFavorites.some(
            (favorite) =>
              favorite?.favorite_type === "product" &&
              String(favorite?.item_id) === productIdAsString
          );

        if (isMounted) {
          setIsFavorite(favorited);
        }
      } catch (fetchError) {
        console.error("Error fetching favorites:", fetchError);
        if (isMounted) {
          setFavoriteError("Неуспешно вчитување на омилениот статус.");
        }
      }
    };

    loadFavoriteState();

    return () => {
      isMounted = false;
    };
  }, [authPayload?.id_token, product?.id]);

  const handleFavoriteToggle = async () => {
    if (!authPayload?.id_token || !product?.id || favoriteBusy) {
      return;
    }

    const nextFavoriteState = !isFavorite;
    setFavoriteBusy(true);
    setFavoriteError("");
    setIsFavorite(nextFavoriteState);

    try {
      if (nextFavoriteState) {
        await addFavorite({
          favoriteType: "product",
          itemId: product.id,
        });
      } else {
        await removeFavorite({
          favoriteType: "product",
          itemId: product.id,
        });
      }

      setFavoriteNotice(
        nextFavoriteState ? "Додадено во омилени." : "Отстрането од омилени."
      );
    } catch (toggleError) {
      console.error("Error updating favorite:", toggleError);
      setIsFavorite(!nextFavoriteState);
      setFavoriteError("Неуспешно ажурирање на омилени.");
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleTrackContact = (cta) => {
    if (!product?.contact_tracking_endpoint || !cta?.channel || !cta?.href) {
      return;
    }

    trackContactClick(
      {
        venue_record_id: product.venue_record_id || null,
        product_id: product.id,
        channel: cta.channel,
        source_context: "product_detail",
        target_url: cta.href,
      },
      product.contact_tracking_endpoint
    );
  };

  const scrollToInquirySection = () => {
    const section = document.querySelector(".product-inquiry-section");
    if (!section) {
      return;
    }

    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleInquirySubmit = async (event) => {
    event.preventDefault();
    if (!product) {
      return;
    }

    setInquiryStatus({ busy: true, error: "", success: "" });

    try {
      await submitLead(
        {
          ...inquiryForm,
          budget: inquiryForm.budget ? Number(inquiryForm.budget) : null,
          product_id: product.id,
          venue_record_id: product.venue_record_id || null,
          source: "product",
        },
        product.inquiry_endpoint
      );

      setInquiryStatus({
        busy: false,
        error: "",
        success: "Вашето барање е испратено. Продавачот ќе ви одговори наскоро.",
      });
      setInquiryForm({
        ...defaultInquiryForm,
        wedding_date: selectedDate || "",
      });
    } catch (submitError) {
      setInquiryStatus({
        busy: false,
        error: submitError.message || "Неуспешно испраќање на барањето.",
        success: "",
      });
    }
  };

  if (loading) {
    return (
      <div className="venue-container product-show">
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={"https://placehold.co/600x400/lightgray/lightgray"}
              alt="Кажав Да"
              className="venue-image"
            />
          </div>
          <div className="venue-summary">
            <span className="venue-eyebrow blurred"></span>
            <h1 className="venue-title blurred">Кажав Да</h1>
            <div className="venue-meta">
              <span className="blurred"></span>
              <span className="blurred"></span>
            </div>
            <p className="venue-description blurred"></p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="venue-container product-show">
        <div className="venue-error-state">
          <p>{error || "Не успеавме да ги вчитаме деталите."}</p>
          <button className="all-products-button" onClick={() => navigate("/all-products")}>
            Назад кон продукти
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="venue-container product-show">
      <motion.div
        className="venue-header"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <div className="venue-header-top">
          <button className="back-button" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </button>
        </div>
        <motion.div className="venue-hero" variants={motionItem}>
          <img
            src={productImages[0]}
            alt={product.name}
            className="venue-image"
            onClick={() => setIsGalleryOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setIsGalleryOpen(true);
              }
            }}
          />
        </motion.div>
        <motion.div className="venue-summary" variants={motionItem}>
          <div className="venue-summary-top">
            <span className="venue-eyebrow">PRODUCT OFFER</span>
            {authPayload?.id_token ? (
              <button
                type="button"
                className={`product-favorite-button ${
                  isFavorite ? "is-active" : ""
                }`}
                onClick={handleFavoriteToggle}
                disabled={favoriteBusy}
                aria-label={
                  isFavorite ? "Отстрани од омилени" : "Додај во омилени"
                }
              >
                {favoriteBusy ? (
                  <CircularProgress size={18} />
                ) : isFavorite ? (
                  <FavoriteIcon fontSize="small" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )}
              </button>
            ) : null}
          </div>
          <h1 className="venue-title">{product.name}</h1>
          <div className="venue-meta">
            {productVenueId ? (
              <Link to={`/venue/${productVenueId}`} className="venue-location-link">
                {product.venue_name}
              </Link>
            ) : (
              <span className="venue-location-link">{product.venue_name}</span>
            )}
            <div className="venue-meta-right">
              {productPrice ? (
                <span className="venue-price">
                  {productPrice}<small> ден</small>
                </span>
              ) : null}
              <span
                className={`venue-availability ${
                  isAvailable ? "is-available" : "is-unavailable"
                }`}
              >
                {isAvailable ? "Достапно" : "Недостапно"}
              </span>
            </div>
          </div>
          <p className="venue-description">{product.description}</p>
          {favoriteError ? (
            <p className="favorite-feedback is-error">{favoriteError}</p>
          ) : null}
          {favoriteNotice ? (
            <p className="favorite-feedback">{favoriteNotice}</p>
          ) : null}
        </motion.div>
      </motion.div>

      <AnimatePresence>
      {isGalleryOpen ? (
        <motion.div
          className="image-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsGalleryOpen(false)}
        >
          <motion.div
            className="image-modal-content"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setIsGalleryOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={productImages[currentImageIndex]}
              alt={`${product.name} ${currentImageIndex + 1}`}
              className="image-modal-image"
            />
            {productImages.length > 1 ? (
              <>
                <div className="image-modal-controls">
                  <button
                    className="image-modal-button"
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex - 1 + productImages.length) %
                          productImages.length
                      )
                    }
                  >
                    ‹
                  </button>
                  <button
                    className="image-modal-button"
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex + 1) % productImages.length
                      )
                    }
                  >
                    ›
                  </button>
                </div>
                <div className="image-modal-dots">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      className={`image-modal-dot ${
                        index === currentImageIndex ? "is-active" : ""
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
      </AnimatePresence>

      <motion.section
        className="product-detail-grid"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <motion.div className="venue-cta-card" variants={motionItem}>
          <div className="venue-section-heading">
            <span className="venue-section-eyebrow">КОНТАКТ</span>
            <h2>Закажете разговор</h2>
          </div>
          <div className="venue-cta-grid">
            {contactCtas.map((cta) =>
              cta.channel === "inquiry" ? (
                <button
                  key={`${cta.channel}-${cta.href}`}
                  type="button"
                  className="venue-cta-button is-primary"
                  onClick={() => {
                    handleTrackContact(cta);
                    scrollToInquirySection();
                  }}
                >
                  {cta.label}
                </button>
              ) : (
                <a
                  key={`${cta.channel}-${cta.href}`}
                  href={cta.href}
                  className="venue-cta-button"
                  onClick={() => handleTrackContact(cta)}
                >
                  {cta.label}
                </a>
              )
            )}
          </div>

          <label className="venue-form-field">
            <span>Провери датум</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => {
                const nextDate = event.target.value;
                setSelectedDate(nextDate);
                setInquiryForm((current) => ({
                  ...current,
                  wedding_date: nextDate,
                }));
              }}
            />
          </label>

          <p className="venue-muted-copy">
            Датумот се чува локално и ќе биде додаден во барањето што го
            испраќате до продавачот.
          </p>
        </motion.div>

        <motion.div className="venue-cta-card" variants={motionItem}>
          <div className="venue-section-heading">
            <span className="venue-section-eyebrow">ПОНУДА</span>
            <h2>Пакети и додатоци</h2>
          </div>

          <div className="product-offer-stack">
            {packages.length > 0 ? (
              <div className="product-offer-group">
                <h3>Пакети</h3>
                {packages.map((pkg) => (
                  <article className="product-offer-card" key={pkg.id}>
                    <div className="product-offer-topline">
                      <strong>{pkg.name}</strong>
                      <span>{formatPrice(pkg.price) || "0"} ден</span>
                    </div>
                    {pkg.description ? <p>{pkg.description}</p> : null}
                    {pkg.minimum_spend ? (
                      <small>
                        Минимална потрошувачка: {formatPrice(pkg.minimum_spend)} ден
                      </small>
                    ) : null}
                  </article>
                ))}
              </div>
            ) : null}

            {addOns.length > 0 ? (
              <div className="product-offer-group">
                <h3>Додатоци</h3>
                {addOns.map((addOn) => (
                  <article className="product-offer-card" key={addOn.id}>
                    <div className="product-offer-topline">
                      <strong>{addOn.name}</strong>
                      <span>{formatPrice(addOn.price) || "0"} ден</span>
                    </div>
                    {addOn.description ? <p>{addOn.description}</p> : null}
                  </article>
                ))}
              </div>
            ) : null}

            {packages.length === 0 && addOns.length === 0 ? (
              <p className="venue-muted-copy">
                Во моментов нема јавно достапни пакети или додатоци за овој продукт.
              </p>
            ) : null}
          </div>
        </motion.div>
      </motion.section>

      <motion.section
        className="venue-planning-section product-inquiry-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <div className="venue-section-heading">
          <span className="venue-section-eyebrow">ПРАШАЊЕ</span>
          <h2>Испратете барање за овој продукт</h2>
        </div>

        <motion.form
          className="venue-form-card"
          onSubmit={handleInquirySubmit}
          variants={motionItem}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: "easeOut" }}
        >
          <div className="venue-form-grid">
            <label className="venue-form-field">
              <span>Име и презиме</span>
              <input
                type="text"
                value={inquiryForm.name}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label className="venue-form-field">
              <span>Email</span>
              <input
                type="email"
                value={inquiryForm.email}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label className="venue-form-field">
              <span>Телефон</span>
              <input
                type="tel"
                value={inquiryForm.phone}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
              />
            </label>
            <label className="venue-form-field">
              <span>Датум на свадба</span>
              <input
                type="date"
                value={inquiryForm.wedding_date}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    wedding_date: event.target.value,
                  }))
                }
              />
            </label>
            <label className="venue-form-field">
              <span>Буџет</span>
              <input
                type="number"
                min="0"
                value={inquiryForm.budget}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    budget: event.target.value,
                  }))
                }
              />
            </label>
            <label className="venue-form-field is-full">
              <span>Порака</span>
              <textarea
                rows="4"
                value={inquiryForm.notes}
                onChange={(event) =>
                  setInquiryForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                placeholder="Опишете што ви е потребно, дали ве интересира пакет и за кој датум прашувате."
              />
            </label>
          </div>

          {inquiryStatus.error ? (
            <p className="venue-form-feedback is-error">{inquiryStatus.error}</p>
          ) : null}
          {inquiryStatus.success ? (
            <p className="venue-form-feedback is-success">
              {inquiryStatus.success}
            </p>
          ) : null}

          <button
            type="submit"
            className="venue-submit-button"
            disabled={inquiryStatus.busy}
          >
            {inquiryStatus.busy ? "Испраќање..." : "Испрати барање"}
          </button>
        </motion.form>
      </motion.section>

      <motion.div
        className="products-section"
        initial="hidden"
        animate="show"
        variants={motionSection}
      >
        <h2 className="products-section-title">Слични производи</h2>
        <Grid
          container
          className="products-list"
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-start"
          alignItems={{ xs: "center", sm: "flex-start" }}
          wrap="wrap"
          sx={{
            px: { xs: 2, sm: 0 },
          }}
        >
          {products.length > 0 ? (
            products.map((similarProduct) => {
              const similarPrice = formatPrice(similarProduct.price);

              return (
                <Grid item size={{ xs: 12, sm: 6, lg: 4 }} key={similarProduct.id}>
                  <Link
                    to={`/product/${similarProduct.id}`}
                    className="product-card-link"
                  >
                    <motion.div
                      className="product-card"
                      variants={motionItem}
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                    >
                      <img
                        src={
                          similarProduct.images?.[0] ||
                          "https://placehold.co/300x200"
                        }
                        alt={similarProduct.name}
                        className="product-image"
                      />
                      <div className="product-card-content">
                        <h3 className="product-title">
                          {similarProduct.name}
                        </h3>
                        <p className="product-price">
                          {similarPrice ? `${similarPrice}` : "—"}{" "}
                          <small>ден</small>
                        </p>
                      </div>
                    </motion.div>
                  </Link>
                </Grid>
              );
            })
          ) : (
            <div className="no-products">Нема производи...</div>
          )}
        </Grid>
        <div className="products-action">
          <Link to="/all-products" className="all-products-button">
            Сите производи
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

export default ProductShow;
